import { eChannelType } from 'src/providers/SocketsProvider/Sockets.enums';
import { RouterPaths } from 'src/components/Router/Router.constants';
import { ChatService } from './Chat.service';
import type { tHumanActionReason, tMarkSurface } from './Chat.types';

/**
 * INT-1538 U9 (KTD10): the one path to `POST .../chat/:cid/human-action`.
 *
 * Every gesture that counts as a person acting on a chat goes through here,
 * and nothing else does: each manual send from the chat, the notice's
 * "Reintentar" (INT-1538 R12), and blocking the contact (INT-1591 D1).
 * Opening, reading, assigning, moving stage, notes, favorites, scheduled sends
 * and forwards never call (R13), and neither does reading the error notice:
 * "Cómo resolverlo", "Código XXXXX" or a template picker opened from it
 * (INT-1604, D4).
 *
 * - Only WhatsApp API chats: messaging tracks only `WHATSAPP_API`, so on any
 *   other channel the call would always be a no-op 204.
 * - The send waits for the call, but never longer than the cap, and never
 *   fails because of it: a timeout or an error leaves the mark on, which is
 *   the accepted safe side. A call that outlives the cap still reaches
 *   messaging; the cap bounds that window (see the plan's Risks).
 * - One gesture, one call: sends of the same chat within a short window (a
 *   drop of several attachments goes out file by file) share the first
 *   call instead of each waiting its own cap in series. A call that failed
 *   frees the window, so the next gesture tries again. A block is its own
 *   gesture: it never shares a send's call, so its reason is not lost.
 * - INT-1602: each call says why (`mensaje` for a send, `bloqueo` for a
 *   block) and from where (`chats` or `embudos`), for the mark's analytics.
 */
export const HUMAN_ACTION_TIMEOUT_MS = 1500;
export const HUMAN_ACTION_DEDUPE_MS = 5000;

const recentCalls = new Map<string, { at: number; call: Promise<void> }>();

/** Tests only: forget the calls of the dedupe window. */
export function resetHumanActionDedupe(): void {
  recentCalls.clear();
}

/**
 * INT-1602: where the person is acting from, read from the route the chat is
 * open on. The funnel board opens the same `<Chat>` in a modal, so the route,
 * not the component, tells the two apart. Undefined anywhere else.
 */
export function currentMarkSurface(
  pathname: string = window.location.pathname,
): tMarkSurface | undefined {
  if (pathname.startsWith(RouterPaths.funnels)) return 'embudos';
  if (pathname.startsWith(RouterPaths.chat)) return 'chats';
  return undefined;
}

type tHumanActionCall = {
  workspaceId: string;
  chatId: string;
  reason: tHumanActionReason;
};

function callOnce({
  workspaceId,
  chatId,
  reason,
}: tHumanActionCall): Promise<void> {
  const key = `${workspaceId}:${chatId}:${reason}`;
  const recent = recentCalls.get(key);
  if (recent && Date.now() - recent.at < HUMAN_ACTION_DEDUPE_MS)
    return recent.call;

  // Deferred so even a synchronous throw lands in the catch.
  const call = Promise.resolve()
    .then(() =>
      ChatService.recordHumanAction({
        workspaceId,
        chatId,
        reason,
        surface: currentMarkSurface(),
      }),
    )
    .catch(() => {
      recentCalls.delete(key);
    });
  recentCalls.set(key, { at: Date.now(), call });
  return call;
}

export type tHumanActionChat =
  | { id?: string | null; integration?: eChannelType | null }
  | null
  | undefined;

type tRecordWithReason = {
  workspaceId: string | null | undefined;
  chat: tHumanActionChat;
  reason: tHumanActionReason;
};

async function recordWithReason({
  workspaceId,
  chat,
  reason,
}: tRecordWithReason): Promise<void> {
  if (
    !workspaceId ||
    !chat?.id ||
    chat.integration !== eChannelType.WHATSAPP_API
  ) {
    return;
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  const cap = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, HUMAN_ACTION_TIMEOUT_MS);
  });

  try {
    await Promise.race([
      callOnce({ workspaceId, chatId: chat.id, reason }),
      cap,
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * A person sent, or is about to send, a message: the reason is `mensaje`.
 * Resolves once messaging answered or the cap elapsed. Never rejects.
 */
export async function recordHumanAction(
  workspaceId: string | null | undefined,
  chat: tHumanActionChat,
): Promise<void> {
  return recordWithReason({ workspaceId, chat, reason: 'mensaje' });
}

/** INT-1591 D1: a person blocked the contact. Never rejects. */
export async function recordBlockHumanAction(
  workspaceId: string | null | undefined,
  chat: tHumanActionChat,
): Promise<void> {
  return recordWithReason({ workspaceId, chat, reason: 'bloqueo' });
}

/** Records the human action, then sends, whatever the call's outcome. */
export async function withHumanAction<T>(
  workspaceId: string | null | undefined,
  chat: tHumanActionChat,
  send: () => T | Promise<T>,
): Promise<T> {
  await recordHumanAction(workspaceId, chat);
  return send();
}
